import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { merge } from 'rxjs';

import { RtComponentOutlet } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types
import { TellentUserAbilitiesSelectors } from '@core/tellent-data'; // FIXME
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import { TellentCurrentUserDataFacade, TellentMembersDataFacade } from '@core/tellent-logic';
import {
  MemberEditTab,
  TeamMemberStatus,
  TellentClientDialogResult,
  TellentDevFlag,
  TellentMember,
  TellentMemberManagedBy,
  TellentOrgAbilities,
} from '@core/tellent-types';
import { resolveMemberFullName } from '@core/tellent-util';
import {
  DialogComponentI,
  DialogHeaderComponent,
  DialogRef,
  ProtectedControlsService,
  TabComponent,
  TabsComponent,
} from '@core/user-interface-legacy';
import { useProtectedControlsCallback } from '@core/user-interface-routing-logic';

import { EditMemberAccountTabComponent } from './edit-member-account-tab/edit-member-account-tab.component';
import type { EditMemberDialogOpenContext } from './edit-member-admin.analytics';
import { EditMemberAnalyticsService } from './edit-member-admin.analytics';
import { EditMemberDepartmentsTabComponent } from './edit-member-departments-tab/edit-member-departments-tab.component';
import { EditMemberGroupsTabComponent } from './edit-member-groups-tab/edit-member-groups-tab.component';
import { EditMemberHeaderComponent } from './edit-member-header/edit-member-header.component';
import { EditMemberPermissionsAccessTabComponent } from './edit-member-permissions-access-tab/edit-member-permissions-access-tab.component';
import { CurrentUserProfileComponent } from '../current-user-profile-dialog/current-user-profile/current-user-profile.component';

export type EditMemberDialogData = {
  memberId: string;
  context: EditMemberDialogOpenContext;
};

type EditMemberDialogResult = TellentClientDialogResult<any>;

@Component({
  selector: 'tlc-edit-member-admin-dialog',
  imports: [
    TranslatePipe,
    EditMemberHeaderComponent,
    TabComponent,
    TabsComponent,
    RtComponentOutlet,
    DialogHeaderComponent,
    InfoboxComponent,
  ],
  providers: [ProtectedControlsService],
  templateUrl: './edit-member-admin-dialog.component.html',
  styleUrl: './edit-member-admin-dialog.component.less',
})
export class EditMemberAdminDialogComponent implements DialogComponentI<
  EditMemberDialogData,
  EditMemberDialogResult
> {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly dialogRef =
    inject<DialogRef<EditMemberDialogData, EditMemberDialogResult>>(DialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly analytics = inject(EditMemberAnalyticsService);

  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  protected readonly groupsFlag = injectDevFlagSignal(TellentDevFlag.UnifiedGroups);
  protected readonly departmentsFlag = injectDevFlagSignal(TellentDevFlag.UnifiedDepartments);
  protected readonly MemberEditTab = MemberEditTab;
  protected readonly TeamMemberStatus = TeamMemberStatus;

  public webComponentOutput: EditMemberDialogResult;

  public memberData: Signal<TellentMember | null>;
  public adminData: Signal<TellentMember>;
  public canManageUsers: Signal<boolean | null>;
  public isSelf: Signal<boolean | null>;
  public fullName: Signal<string | null>;
  public memberInputs: {
    memberGuid: string;
  } = {
    memberGuid: '',
  };

  public activeComponent = signal<MemberEditTab>(MemberEditTab.Account);
  public componentMap = new Map<MemberEditTab, any>([
    [MemberEditTab.Account, EditMemberAccountTabComponent],
    [MemberEditTab.EditProfile, CurrentUserProfileComponent],
    [MemberEditTab.Groups, EditMemberGroupsTabComponent],
    [MemberEditTab.Departments, EditMemberDepartmentsTabComponent],
    [MemberEditTab.PermissionsAccess, EditMemberPermissionsAccessTabComponent],
  ]);

  public protectedControls = useProtectedControlsCallback();

  protected isExternallyManaged: Signal<boolean>;
  protected isOktaManaged: Signal<boolean>;

  public ngOnInit(): void {
    merge(
      this.membersDataFacade.deactivateMemberSuccess$,
      this.membersDataFacade.reactivateMemberSuccess$,
      this.membersDataFacade.sendInvitationSuccess$,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.membersDataFacade.fetchOne(this.memberData()?.guid!);
      });

    this.membersDataFacade.inviteExistingMemberSuccess$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dialogRef.close();
      });
  }

  public setDialogData(data: EditMemberDialogData, triggerUpdate: boolean): void {
    this.membersDataFacade.fetchOne(data.memberId);
    this.memberData = this.membersDataFacade.teamMemberById(data.memberId);

    this.adminData = this.currentUserFacade.organizationMembership;

    this.isSelf = computed(() => {
      const memberData = this.memberData();
      const adminData = this.adminData();

      if (memberData && adminData) {
        return memberData.userGuid === adminData.userGuid;
      }

      return null;
    });

    this.activeComponent.set(this.isSelf() ? MemberEditTab.EditProfile : MemberEditTab.Account);

    this.canManageUsers = this.store.selectSignal(
      TellentUserAbilitiesSelectors.hasOne(TellentOrgAbilities.ManageUsers),
    );

    this.fullName = computed(() => {
      const memberData = this.memberData();
      return memberData ? resolveMemberFullName(memberData) : null;
    });

    this.isExternallyManaged = computed(() => {
      const memberData = this.memberData();
      return !!memberData && memberData.managedBy !== TellentMemberManagedBy.Tellent;
    });

    this.isOktaManaged = computed(
      () => this.memberData()?.managedBy === TellentMemberManagedBy.Okta,
    );

    this.memberInputs.memberGuid = data.memberId;
    this.cdRef.detectChanges();

    this.analytics.trackDialogOpen(data.context);
  }

  public setActiveTab(memberEditTab: MemberEditTab) {
    this.protectedControls(() => {
      this.activeComponent.set(memberEditTab);
      this.analytics.trackSelectedTab(memberEditTab);
    }).subscribe();
  }
  public close() {
    this.dialogRef.close();
  }
}
